import React, { useState, useEffect, useMemo } from 'react';
import { API_BASE_URL } from '../config/api';

const API = API_BASE_URL;

const CHANNEL = {
  email: { label: 'Email', cls: 'bg-blue-100 text-blue-700' },
  sms: { label: 'SMS', cls: 'bg-green-100 text-green-700' },
  task: { label: 'Task', cls: 'bg-purple-100 text-purple-700' },
};

const fmtWhen = (ts) => {
  const d = new Date(ts);
  const now = new Date();
  const sameDay = d.toDateString() === now.toDateString();
  const time = d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
  if (sameDay) return `Today ${time}`;
  const tmr = new Date(now); tmr.setDate(now.getDate() + 1);
  if (d.toDateString() === tmr.toDateString()) return `Tomorrow ${time}`;
  return `${d.toLocaleDateString([], { month: 'short', day: 'numeric' })} ${time}`;
};

// Short tz abbreviation (e.g. "PT", "ET") for a given IANA zone at a time.
const tzAbbr = (ts, tz) => {
  try {
    const parts = new Intl.DateTimeFormat('en-US', { timeZone: tz, timeZoneName: 'short' }).formatToParts(new Date(ts));
    return parts.find(p => p.type === 'timeZoneName')?.value || tz.split('/').pop();
  } catch { return (tz || '').split('/').pop(); }
};
// Time-of-day in a specific tz, e.g. "9:00 AM".
const timeInTz = (ts, tz) => {
  try { return new Date(ts).toLocaleTimeString('en-US', { timeZone: tz, hour: 'numeric', minute: '2-digit' }); }
  catch { return ''; }
};

const dayKey = (ts) => new Date(ts).toLocaleDateString([], { weekday: 'long', month: 'short', day: 'numeric' });

/**
 * OutboxTab — the firm's materialized send queue in time order. Shows every
 * scheduled email/sms/task across all campaigns. Drag a row to pin it to a new
 * time (the rest re-flow around it); click to edit the message; cancel to skip.
 */
const OutboxTab = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [scope, setScope] = useState('firm'); // firm | mine
  const [channelFilter, setChannelFilter] = useState('all'); // all | email | sms | task
  const [editing, setEditing] = useState(null); // the item being edited
  const [dragId, setDragId] = useState(null);

  const token = localStorage.getItem('authToken');
  const headers = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };

  const load = async () => {
    try {
      const qs = scope === 'mine' ? '?scope=mine' : '';
      const res = await fetch(`${API}/api/outreach/outbox${qs}`, { headers });
      if (res.ok) setItems(await res.json());
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [scope]);

  const rebuild = async () => {
    setLoading(true);
    try { await fetch(`${API}/api/outreach/outbox/rebuild`, { method: 'POST', headers }); } catch (e) { console.error(e); }
    load();
  };

  const saveEdit = async () => {
    if (!editing) return;
    try {
      await fetch(`${API}/api/outreach/outbox/${editing.id}`, {
        method: 'PUT', headers, body: JSON.stringify({ subject: editing.subject, body: editing.body })
      });
      setItems(prev => prev.map(i => i.id === editing.id ? { ...i, subject: editing.subject, body: editing.body, edited: true } : i));
      setEditing(null);
    } catch (e) { console.error(e); }
  };

  const cancelItem = async (id) => {
    if (!window.confirm('Cancel this scheduled send? The step will be skipped.')) return;
    setItems(prev => prev.filter(i => i.id !== id));
    try { await fetch(`${API}/api/outreach/outbox/${id}`, { method: 'DELETE', headers }); } catch (e) { console.error(e); }
  };

  // Drop row A before row B → pin A to just before B's time.
  const onDrop = async (targetItem) => {
    if (!dragId || dragId === targetItem.id) { setDragId(null); return; }
    const newTime = new Date(new Date(targetItem.scheduled_at).getTime() - 60000).toISOString();
    setDragId(null);
    try {
      await fetch(`${API}/api/outreach/outbox/${dragId}/schedule`, {
        method: 'PUT', headers, body: JSON.stringify({ scheduled_at: newTime, pinned: true })
      });
      load();
    } catch (e) { console.error(e); }
  };

  const filteredItems = useMemo(
    () => channelFilter === 'all' ? items : items.filter(it => it.channel === channelFilter),
    [items, channelFilter]
  );
  const channelCounts = useMemo(() => {
    const c = { all: items.length, email: 0, sms: 0 };
    for (const it of items) if (c[it.channel] != null) c[it.channel]++;
    return c;
  }, [items]);

  const grouped = useMemo(() => {
    const g = {};
    for (const it of filteredItems) { const k = dayKey(it.scheduled_at); (g[k] = g[k] || []).push(it); }
    return g;
  }, [filteredItems]);

  return (
    <div className="flex-1 min-h-0 flex flex-col overflow-hidden">
      {/* Toolbar — pinned at top with its own padding (matches Campaigns tab) */}
      <div className="flex-shrink-0 flex items-center gap-2 mb-0 flex-wrap px-3 pt-2 pb-2">
        <div className="inline-flex h-[30px] border border-gray-300 rounded overflow-hidden bg-white">
          <button onClick={() => setScope('firm')}
            className={`px-3 text-sm transition-colors ${scope === 'firm' ? 'bg-emerald-50 text-emerald-700 font-medium' : 'text-gray-600 hover:bg-gray-50'}`}>Whole firm</button>
          <button onClick={() => setScope('mine')}
            className={`px-3 text-sm border-l border-gray-300 transition-colors ${scope === 'mine' ? 'bg-emerald-50 text-emerald-700 font-medium' : 'text-gray-600 hover:bg-gray-50'}`}>Just mine</button>
        </div>
        {/* Channel filter */}
        <div className="inline-flex h-[30px] border border-gray-300 rounded overflow-hidden bg-white">
          {[
            { key: 'all', label: 'All' },
            { key: 'email', label: 'Email' },
            { key: 'sms', label: 'SMS' },
          ].map((t, i) => (
            <button key={t.key} onClick={() => setChannelFilter(t.key)}
              className={`px-3 text-sm flex items-center gap-1.5 transition-colors ${i > 0 ? 'border-l border-gray-300' : ''} ${
                channelFilter === t.key ? 'bg-emerald-50 text-emerald-700 font-medium' : 'text-gray-600 hover:bg-gray-50'
              }`}>
              {t.label}{channelCounts[t.key] > 0 && <span className={`text-[10px] ${channelFilter === t.key ? 'text-emerald-700/70' : 'text-gray-400'}`}>{channelCounts[t.key]}</span>}
            </button>
          ))}
        </div>
        <span className="text-xs text-gray-400">{filteredItems.length} scheduled</span>
        <button onClick={rebuild} className="ml-auto h-[30px] px-3 text-sm border border-gray-300 rounded text-gray-600 hover:bg-gray-50 flex items-center gap-1.5">
          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" /></svg>
          Recompute order
        </button>
      </div>

      <div className="flex-1 overflow-y-auto px-3 pb-4">
      {loading ? (
        <div className="flex items-center justify-center py-16"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600" /></div>
      ) : filteredItems.length === 0 ? (
        <div className="bg-white rounded-lg border shadow-sm p-12 text-center">
          <p className="text-gray-500 font-medium">Nothing scheduled</p>
          <p className="text-sm text-gray-400 mt-1">As candidates progress through campaigns, their next sends appear here in order.</p>
        </div>
      ) : (
        <div className="space-y-4">
          {Object.entries(grouped).map(([day, rows]) => (
            <div key={day}>
              <div className="text-[11px] font-semibold text-gray-400 uppercase tracking-wide mb-1.5 px-1">{day}</div>
              <div className="bg-white rounded-lg border shadow-sm overflow-hidden">
                {rows.map(it => {
                  const ch = CHANNEL[it.channel] || CHANNEL.email;
                  return (
                    <div key={it.id}
                      draggable
                      onDragStart={() => setDragId(it.id)}
                      onDragOver={(e) => e.preventDefault()}
                      onDrop={() => onDrop(it)}
                      className={`flex items-center gap-3 px-3 py-2.5 border-b last:border-b-0 hover:bg-gray-50 transition-colors ${dragId === it.id ? 'opacity-40' : ''}`}>
                      <svg className="w-3.5 h-3.5 text-gray-300 cursor-grab flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8h16M4 16h16" /></svg>
                      <div className="w-24 flex-shrink-0">
                        <div className="text-xs text-gray-700 flex items-center">
                          {fmtWhen(it.scheduled_at)}
                          {it.pinned && <span className="ml-1 text-[9px] text-amber-500" title="Manually pinned">📌</span>}
                        </div>
                        {it.recipient_tz && it.recipient_tz !== it.org_tz && (
                          <div className="text-[9px] text-emerald-600 leading-tight" title={`Recipient (${it.recipient_tz}) · You (${it.org_tz})`}>
                            {timeInTz(it.scheduled_at, it.recipient_tz)} {tzAbbr(it.scheduled_at, it.recipient_tz)}
                          </div>
                        )}
                      </div>
                      <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium flex-shrink-0 ${ch.cls}`}>{ch.label}</span>
                      <div className="min-w-0 flex-1">
                        <div className="text-sm font-medium text-gray-900 truncate">{it.candidate_name || 'Unknown'}</div>
                        <div className="text-[11px] text-gray-400 truncate">
                          {it.campaign_name}{it.subject ? ` · ${it.subject}` : ''}{it.edited ? ' · edited' : ''}
                        </div>
                      </div>
                      {scope === 'firm' && it.owner_name && <span className="text-[11px] text-gray-400 flex-shrink-0">{it.owner_name}</span>}
                      <div className="flex items-center gap-1 flex-shrink-0">
                        {it.channel !== 'task' && (
                          <button onClick={() => setEditing({ ...it })} className="p-1 text-gray-400 hover:text-gray-700 rounded hover:bg-gray-100" title="Edit message">
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" /></svg>
                          </button>
                        )}
                        <button onClick={() => cancelItem(it.id)} className="p-1 text-gray-400 hover:text-red-500 rounded hover:bg-gray-100" title="Cancel send">
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}
      </div>

      {/* Edit message modal */}
      {editing && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4" onClick={() => setEditing(null)}>
          <div className="bg-white rounded-lg shadow-xl w-full max-w-lg" onClick={e => e.stopPropagation()}>
            <div className="px-5 py-3 border-b flex items-center justify-between">
              <h3 className="font-semibold text-gray-900 text-sm">Edit message — {editing.candidate_name}</h3>
              <span className="text-xs text-gray-400">{fmtWhen(editing.scheduled_at)}</span>
            </div>
            <div className="p-5 space-y-3">
              {editing.channel === 'email' && (
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Subject</label>
                  <input value={editing.subject || ''} onChange={e => setEditing({ ...editing, subject: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg text-sm" />
                </div>
              )}
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Message</label>
                <textarea value={editing.body || ''} onChange={e => setEditing({ ...editing, body: e.target.value })}
                  rows={7} className="w-full px-3 py-2 border rounded-lg text-sm resize-none font-mono" />
                <p className="text-[10px] text-gray-400 mt-1">Merge fields like {'{{first_name}}'} still resolve at send time.</p>
              </div>
            </div>
            <div className="px-5 py-3 border-t flex justify-end gap-2">
              <button onClick={() => setEditing(null)} className="px-4 py-2 text-sm border rounded-lg hover:bg-gray-50">Cancel</button>
              <button onClick={saveEdit} className="px-4 py-2 text-sm bg-emerald-600 text-white rounded hover:bg-emerald-700">Save message</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default OutboxTab;
